import type { MarketingOpportunityItem } from '../types';

const CORE_KEYS = new Set([
  'title',
  'category',
  'rationale',
  'urgency',
  'suggested_action',
  'suggestedAction',
  'action',
  'opportunity',
  'name',
  'signal',
  'headline',
  'topic',
  'type',
  'reason',
  'description',
  'priority',
  'text',
  'recommendation',
  'skip_reason',
  'skipReason',
  'why_not',
  'exclusion_reason',
  'excluded_reason',
  'why_excluded',
]);

const OUTREACH_FIELD_LABELS: Record<string, string> = {
  contact: 'Contact',
  contact_name: 'Contact',
  target: 'Target',
  target_name: 'Target',
  company: 'Company',
  organization: 'Organization',
  org: 'Organization',
  who_to_contact: 'Who to contact',
  outreach_target: 'Outreach target',
  recommended_contact: 'Recommended contact',
  email: 'Email',
  phone: 'Phone',
  url: 'Link',
  link: 'Link',
  website: 'Website',
  location: 'Location',
  region: 'Region',
  deadline: 'Deadline',
  timing: 'Timing',
};

const OUTREACH_FIELD_ORDER = [
  'who_to_contact',
  'contact',
  'contact_name',
  'outreach_target',
  'target',
  'target_name',
  'recommended_contact',
  'company',
  'organization',
  'org',
  'email',
  'phone',
  'location',
  'region',
  'website',
  'url',
  'link',
  'deadline',
  'timing',
];

export type OutreachField = { key: string; label: string; value: string };

export function urgencyRank(urgency: string | undefined): number {
  const u = (urgency || '').toLowerCase();
  if (u.includes('high') || u.includes('urgent')) return 0;
  if (u.includes('low')) return 2;
  return 1;
}

export function urgencyLabel(urgency: string | undefined): string {
  const u = (urgency || '').toLowerCase();
  if (u.includes('high') || u.includes('urgent')) return 'High priority';
  if (u.includes('low')) return 'Low priority';
  return urgency?.trim() || 'Medium priority';
}

export function urgencyClassName(urgency: string | undefined): 'high' | 'medium' | 'low' {
  const rank = urgencyRank(urgency);
  if (rank === 0) return 'high';
  if (rank === 2) return 'low';
  return 'medium';
}

function formatFieldValue(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

export function extractSkipReason(item: MarketingOpportunityItem): string | undefined {
  for (const key of [
    'skip_reason',
    'why_not',
    'skipReason',
    'exclusion_reason',
    'excluded_reason',
    'why_excluded',
    'reason',
    'rationale',
  ]) {
    const v = formatFieldValue(item[key]);
    if (v) return v;
  }
  return undefined;
}

export function extractOutreachAction(item: MarketingOpportunityItem): string | undefined {
  for (const key of ['suggested_action', 'suggestedAction', 'action', 'next_step', 'outreach_action']) {
    const v = formatFieldValue(item[key]);
    if (v) return v;
  }
  return undefined;
}

export function extractOutreachFields(item: MarketingOpportunityItem): OutreachField[] {
  const seen = new Set<string>();
  const fields: OutreachField[] = [];

  for (const key of OUTREACH_FIELD_ORDER) {
    const value = formatFieldValue(item[key]);
    if (!value || seen.has(value.toLowerCase())) continue;
    seen.add(value.toLowerCase());
    fields.push({
      key,
      label: OUTREACH_FIELD_LABELS[key] || key.replace(/_/g, ' '),
      value,
    });
  }

  for (const [key, raw] of Object.entries(item)) {
    if (CORE_KEYS.has(key) || OUTREACH_FIELD_LABELS[key]) continue;
    const value = formatFieldValue(raw);
    if (!value || seen.has(value.toLowerCase())) continue;
    const k = key.toLowerCase();
    if (
      k.includes('contact') ||
      k.includes('target') ||
      k.includes('email') ||
      k.includes('phone') ||
      k.includes('url') ||
      k.includes('link') ||
      k.includes('company') ||
      k.includes('deadline')
    ) {
      seen.add(value.toLowerCase());
      fields.push({
        key,
        label: key.replace(/_/g, ' '),
        value,
      });
    }
  }

  return fields;
}

export function extractCardLocation(item: MarketingOpportunityItem): string | undefined {
  const fields = extractOutreachFields(item);
  return fields.find(f => f.key === 'location' || f.key === 'region')?.value;
}

export function extractDetailFields(item: MarketingOpportunityItem): OutreachField[] {
  const outreachKeys = new Set(extractOutreachFields(item).map(f => f.key));
  const details: OutreachField[] = [];

  for (const [key, raw] of Object.entries(item)) {
    if (CORE_KEYS.has(key) || outreachKeys.has(key)) continue;
    const value = formatFieldValue(raw);
    if (!value) continue;
    details.push({ key, label: key.replace(/_/g, ' '), value });
  }

  return details;
}

export function sortOpportunitiesForOutreach(items: MarketingOpportunityItem[]): MarketingOpportunityItem[] {
  return [...items].sort((a, b) => {
    const byUrgency = urgencyRank(a.urgency) - urgencyRank(b.urgency);
    if (byUrgency !== 0) return byUrgency;
    return a.title.localeCompare(b.title);
  });
}

export function buildOutreachBrief(
  item: MarketingOpportunityItem,
  index: number,
  reportDate: string,
): string {
  const lines: string[] = [
    `#${index + 1} — ${item.title}`,
    `Report date: ${reportDate}`,
  ];
  if (item.category) lines.push(`Category: ${item.category}`);
  lines.push(`Priority: ${urgencyLabel(item.urgency)}`);
  if (item.rationale) lines.push('', 'Why this opportunity:', item.rationale);
  const action = extractOutreachAction(item);
  if (action) lines.push('', 'EA outreach action:', action);
  const fields = extractOutreachFields(item);
  if (fields.length) {
    lines.push('', 'Contact / target details:');
    for (const f of fields) lines.push(`- ${f.label}: ${f.value}`);
  }
  return lines.join('\n');
}
